import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { TrendingUp, CalendarDays, Sparkles } from 'lucide-react';

const BRAND = '#0B4F3F';
const ACCENT = '#0B4F3F';
const RED = '#B91C1C';
const inr = (n) => `₹${(Math.round((n || 0) * 100) / 100).toLocaleString('en-IN')}`;

function ChartCard({ children, height = 200 }) {
  return (
    <div className="h-[200px]" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </div>
  );
}

function TrendBar({ data, dataKey, labelKey, dynamicColor = false }) {
  return (
    <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#E4E8EF" vertical={false} />
      <XAxis
        dataKey={labelKey}
        tick={{ fill: '#5B6675', fontSize: 9 }}
        interval="preserveStartEnd"
      />
      <YAxis tick={{ fill: '#5B6675', fontSize: 9 }} />
      <Tooltip
        cursor={{ fill: 'rgba(0,76,38,0.06)' }}
        contentStyle={{ borderRadius: 12, border: '1px solid #E4E8EF', fontSize: 12 }}
        formatter={(v) => [inr(v), 'Profit']}
      />
      <Bar dataKey={dataKey} radius={[4, 4, 0, 0]}>
        {data.map((entry, i) => {
          let fill = BRAND;
          if (dynamicColor) {
            fill = entry[dataKey] < 0 ? RED : entry[dataKey] === 0 ? '#E4E8EF' : BRAND;
          }
          return <Cell key={i} fill={fill} />;
        })}
      </Bar>
    </BarChart>
  );
}

export default function InventoryTrends({ refreshKey = 0 }) {
  const { api } = useAuth();
  const [daily, setDaily] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([
        api.get('/admin/inventory/trends/daily', { params: { days: 30 } }),
        api.get('/admin/inventory/trends/monthly', { params: { months: 12 } }),
      ]);
      setDaily(d.data);
      setMonthly(m.data);
    } catch {} finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return <div className="h-48 bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] animate-pulse" />;
  }

  const profit = daily?.totals?.profit || 0;
  const profitColor = profit > 0 ? 'text-[#15803D]' : profit < 0 ? 'text-[#B91C1C]' : 'text-[#0B1620]';

  return (
    <div className="bg-[#F6F8FB] border border-[#E4E8EF] rounded-2xl p-3" data-testid="inventory-trends">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4 text-[#0B4F3F]" />
        <h3 className="text-sm font-semibold text-[#0B1620] font-['Bricolage_Grotesque']">P/L Trend</h3>
        <span className={`ml-auto text-xs font-bold ${profitColor}`} data-testid="trend-30d-profit">
          {inr(profit)} <span className="text-[10px] font-normal text-[#5B6675]">/ 30d</span>
        </span>
      </div>

      <Tabs defaultValue="daily">
        <TabsList className="w-full bg-[#EFF2F7] rounded-lg p-1 h-auto">
          <TabsTrigger
            value="daily"
            data-testid="trend-tab-daily"
            className="flex-1 rounded-md text-[11px] gap-1 data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm"
          >
            <CalendarDays className="w-3 h-3" /> Last 30 days
          </TabsTrigger>
          <TabsTrigger
            value="monthly"
            data-testid="trend-tab-monthly"
            className="flex-1 rounded-md text-[11px] gap-1 data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm"
          >
            <CalendarDays className="w-3 h-3" /> Last 12 months
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="mt-3">
          <ChartCard>
            <TrendBar data={daily?.series || []} dataKey="profit" labelKey="label" dynamicColor />
          </ChartCard>
          {daily?.best_day?.profit > 0 && (
            <p className="text-[11px] text-[#5B6675] mt-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#0B4F3F]" />
              Best day: <span className="font-semibold text-[#0B1620]">{daily.best_day.label}</span> · {inr(daily.best_day.profit)}
            </p>
          )}
        </TabsContent>

        <TabsContent value="monthly" className="mt-3">
          <ChartCard>
            <TrendBar data={monthly?.series || []} dataKey="profit" labelKey="label" dynamicColor />
          </ChartCard>
          {monthly?.best_month?.profit > 0 && (
            <p className="text-[11px] text-[#5B6675] mt-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#0B4F3F]" />
              Best month: <span className="font-semibold text-[#0B1620]">{monthly.best_month.label}</span> · {inr(monthly.best_month.profit)}
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
